import { h } from "preact";
import { useEffect, useState, useRef } from "preact/hooks";
import { ArrowDown, SelectedCheckSmall } from "../helpers/icons";
import RichText from "../helpers/richText";

export default function AliasedOption({
  className = "",
  product,
  option,
  selectedOptions,
  handleVariantChange,
  active,
  direction = "down",
}) {
  const [idPrefix] = useState(window.crypto.randomUUID());
  const [selectedAlias, setSelectedAlias] = useState(null);
  const listboxRef = useRef(null);
  const indeterminate = active && !selectedAlias;

  const selectedOptionValues = selectedOptions.flatMap((selectedOption) =>
    Object.values(selectedOption),
  );

  let instructions = null;

  product.variants.edges.find(({ node }) => {
    const instructionsValue =
      node.variantAliases?.reference.instructions?.value;

    if (instructionsValue) {
      instructions = instructionsValue;
    }

    return instructionsValue;
  });

  const aliasedValues = product.variants.edges
    .filter(({ node }) =>
      node.selectedOptions.every((selectedOption, i) => {
        if (i === selectedOptions.length - 1) return true;

        return selectedOptionValues[i] == selectedOption.value;
      }),
    )
    .flatMap(({ node }) => {
      const lastOption = node.selectedOptions[node.selectedOptions.length - 1];

      if (!node.variantAliases) {
        return {
          alias: lastOption.value,
          optionValue: lastOption.value,
        };
      }

      const aliases = JSON.parse(node.variantAliases.reference.aliases.value);

      return aliases.map((alias) => ({
        alias,
        optionValue: lastOption.value,
      }));
    })
    .sort((a, b) => {
      // Sort alphabetically
      const nameA = a.alias.toUpperCase();
      const nameB = b.alias.toUpperCase();
      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }

      // names must be equal
      return 0;
    });

  useEffect(() => {
    if (!listboxRef.current) return;

    const handleChange = (e) => {
      setSelectedAlias(e.detail.value);

      const aliasedValue = aliasedValues.find(
        ({ alias }) => alias === e.detail.value,
      );
      if (!aliasedValue) return;

      handleVariantChange(option?.name, aliasedValue.optionValue);
    };

    listboxRef.current.addEventListener("aria-listbox:change", handleChange);

    return () =>
      listboxRef.current.removeEventListener(
        "aria-listbox:change",
        handleChange,
      );
  }, [listboxRef, setSelectedAlias, handleVariantChange]);

  return (
    <div
      className={`product-form__aliased-option ${className} product-form__aliased-option--opens-${direction}`}
    >
      <button
        type="button"
        className="
          product-form__aliased-option-combobox-trigger
        "
        role="combobox"
        aria-haspopup
        aria-controls={`${idPrefix}-aliased-option-listbox`}
      >
        <aria-combobox-trigger
          data-controls={`${idPrefix}-aliased-option-combobox`}
        ></aria-combobox-trigger>
        <div
          className={`product-form__aliased-option-combobox-trigger-text ${indeterminate ? "product-form__aliased-option-combobox-trigger-text--indeterminate" : ""}`}
        >
          {selectedAlias || `Select ${option.name}`}
        </div>
        <ArrowDown />
      </button>
      <aria-combobox
        id={`${idPrefix}-aliased-option-combobox`}
        className="product-form__aliased-option-combobox"
        data-content
        style="display: none;"
        close-on-change-when="screen"
      >
        <fieldset className="product-form__aliased-option-fieldset">
          <legend className="visually-hidden">{option.name}</legend>
          <aria-listbox-v2 ref={listboxRef}>
            <ul
              id={`${idPrefix}-aliased-option-listbox`}
              role="listbox"
              className="product-form__aliased-option-listbox-list"
            >
              {aliasedValues.map(({ alias, optionValue }, i) => (
                <li
                  className="product-form__aliased-option-listbox-option"
                  role="option"
                  id={`${idPrefix}-aliased-option-listbox-option-${i}`}
                  data-value={alias}
                  aria-selected={selectedAlias === alias}
                >
                  <div className="product-form__aliased-option-listbox-option-text">
                    {alias}
                    <SelectedCheckSmall className="product-form__aliased-option-listbox-option-checkmark" />
                  </div>
                </li>
              ))}
            </ul>
          </aria-listbox-v2>
        </fieldset>
      </aria-combobox>
      {instructions && (
        <RichText
          schema={instructions}
          className="product-form__aliased-option-instructions rte--simple"
        />
      )}
      {indeterminate && (
        <div className="product-form__aliased-option-indeterminate-error">
          {`Please select your ${option.name}`}
        </div>
      )}
    </div>
  );
}
